import React, { useState, useEffect, useMemo } from "react";
import { Select, message, Spin, Space, Tooltip, Radio, Row, Col, Checkbox } from "antd";
import { airportApi } from "../../api/masterDataApi";

// Module-level cache
let cachedAirports = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

const fetchAirportData = async () => {
    const now = Date.now();
    if (cachedAirports && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
        return cachedAirports;
    }

    try {
        const response = await airportApi.getAirportList({ page_size: 500 });
        const airportList = response?.data?.items || [];
        if (airportList.length > 0) {
            cachedAirports = airportList;
            cacheTimestamp = now;
            return airportList;
        }
        return [];
    } catch (error) {
        console.error("获取机场列表失败:", error);
        message.error("获取机场列表失败");
        // Return old cache if available, otherwise empty array
        return cachedAirports || [];
    }
};

/**
 * 可复用的机场选择器组件
 * @param {object} props - 组件属性，透传给 antd Select 组件
 * @param {string | number} props.value - 当前选中的机场ID或IATA代码 (取决于 valueType)
 * @param {function} props.onChange - 选中项变化时的回调函数
 * @param {'id' | 'iata_code'} [props.valueType='id'] - `value` 和 `onChange` 回调参数是机场ID还是IATA代码
 * @param {'simple' | 'detailed'} [props.displayMode='simple'] - 显示模式
 *        - simple: 只显示 IATA代码 + 名称
 *        - detailed: 显示 IATA代码 + 名称 + 城市/国家等信息
 * @param {function} [props.renderLabel] - 自定义渲染标签的函数 (airport) => ReactNode
 * @param {boolean} [props.showRelationCounts=false] - 是否显示关联数量统计
 * @param {boolean} [props.tiled=false] - 是否使用平铺模式 (Radio.Group) 而不是下拉选择 (Select)
 * @param {number} [props.tiledColumns=4] - 平铺模式下每行显示的列数
 * @param {string} [props.tiledSize='middle'] - 平铺模式下 Radio.Button 的大小，可选 'small', 'middle', 'large'
 * @param {number} [props.maxRows] - 平铺模式下最大显示行数，超出后显示滚动条
 * @param {boolean} [props.showIcao=false] - 是否显示ICAO代码
 * @param {Array} [props.airports] - 外部传入的机场数据；未传入时组件自行加载
 */
const AirportSelector = ({ valueType = "id", displayMode = "simple", renderLabel, showRelationCounts = false, tiled = false, tiledColumns = 4, tiledSize = "middle", maxRows, showIcao = false, airports, onChange, ...restProps }) => {
    const [airportOptions, setAirportOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (Array.isArray(airports)) {
            setAirportOptions(airports);
            setLoading(false);
            return undefined;
        }

        let isMounted = true;
        const loadAirports = async () => {
            setLoading(true);
            const data = await fetchAirportData();
            if (isMounted) {
                setAirportOptions(data);
                setLoading(false);
            }
        };
        loadAirports();
        return () => {
            isMounted = false;
        };
    }, [airports]);

    // 默认标签渲染
    const defaultRenderLabel = (airport) => {
        if (displayMode === "detailed") {
            return (
                <Space>
                    <span style={{ color: "#1890ff", fontWeight: "bold" }}>{airport.iata_code}</span>
                    <span>{airport.name}</span>
                    {airport.city && <span style={{ color: "#666", fontSize: "12px" }}>{airport.city}</span>}
                    {showRelationCounts && airport.relation_counts && (
                        <Tooltip title="关联数量统计">
                            <span style={{ color: "#999", fontSize: "11px" }}>({Object.values(airport.relation_counts).reduce((a, b) => a + b, 0)} 关联)</span>
                        </Tooltip>
                    )}
                </Space>
            );
        }

        // simple mode
        return (
            <Space>
                <span style={{ color: "#1890ff", fontWeight: "bold" }}>{airport.iata_code}</span>
                <span>{airport.name}</span>
                {showRelationCounts && airport.relation_counts && <span style={{ color: "#999", fontSize: "11px" }}>({Object.values(airport.relation_counts).reduce((a, b) => a + b, 0)})</span>}
            </Space>
        );
    };

    const options = useMemo(() => {
        return airportOptions.map((airport) => ({
            value: valueType === "iata_code" ? airport.iata_code : airport.id,
            label: renderLabel ? renderLabel(airport) : defaultRenderLabel(airport),
            // 保存原始数据用于筛选
            airport: airport,
            // For filtering
            searchValue: `${airport.iata_code} ${airport.name} ${airport.city || ""} ${airport.country || ""}`.toLowerCase(),
        }));
    }, [airportOptions, valueType, displayMode, showRelationCounts, renderLabel]);

    const filterOption = (input, option) => {
        return option?.searchValue?.includes(input.toLowerCase());
    };

    // 处理选择变更，确保传递option参数
    const handleChange = (value, option) => {
        if (onChange) {
            onChange(value, option);
        }
    };

    // 平铺模式下的变更处理
    const handleTiledChange = (e) => {
        const value = e.target.value;
        // 找到对应的option
        const option = options.find((opt) => opt.value === value);
        if (onChange) {
            onChange(value, option);
        }
    };

    // 多选平铺模式下的变更处理
    const handleTiledMultipleChange = (checkedValue) => {
        if (onChange) {
            // 获取所有选中选项的详细信息
            const selectedOptions = options.filter((opt) => checkedValue.includes(opt.value));
            onChange(checkedValue, selectedOptions);
        }
    };

    // 为平铺模式准备的紧凑渲染函数
    const compactRenderLabel = (airport) => {
        return (
            <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center", 
                textAlign: "center", 
                minHeight: "32px", 
                justifyContent: "center",
                padding: "2px"
            }}>
                <div style={{ 
                    fontWeight: "bold", 
                    color: "#1890ff", 
                    fontSize: "12px",
                    lineHeight: "1.2"
                }}>
                    {airport.iata_code}
                    {showIcao && airport.icao_code && (
                        <span style={{ fontSize: "10px", color: "#999", marginLeft: "2px" }}>
                            /{airport.icao_code}
                        </span>
                    )}
                </div>
                <div style={{ 
                    fontSize: "10px", 
                    color: "#666", 
                    marginTop: "1px", 
                    lineHeight: "1.1",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%"
                }}>
                    {airport.name}
                </div>
                {showRelationCounts && airport.relation_counts && (
                    <div style={{ fontSize: "9px", color: "#999", marginTop: "1px" }}>
                        ({Object.values(airport.relation_counts).reduce((a, b) => a + b, 0)})
                    </div>
                )}
            </div>
        );
    };

    // 如果是平铺模式
    if (tiled) {
        // 多选模式
        if (restProps.mode === "multiple") {
            return (
                <div style={{ width: "100%" }}>
                    <Spin spinning={loading}>
                        <Checkbox.Group value={restProps.value} onChange={handleTiledMultipleChange} style={{ width: "100%" }}>
                            <Row gutter={[8, 8]}>
                                {options.map((option) => (
                                    <Col span={24 / tiledColumns} key={option.value}>
                                        <Checkbox
                                            value={option.value}
                                            style={{
                                                width: "100%",
                                                minWidth: 0,
                                                marginRight: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    padding: "8px 4px",
                                                    border: "1px solid #d9d9d9",
                                                    borderRadius: "4px",
                                                    textAlign: "center",
                                                    width: "100%",
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    minHeight: "60px",
                                                }}
                                            >
                                                {compactRenderLabel(option.airport)}
                                            </div>
                                        </Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </Spin>
                </div>
            );
        }

        // 单选模式
        return (
            <div style={{ width: "100%" }}>
                <Spin spinning={loading}>
                    <Radio.Group size={tiledSize} value={restProps.value} onChange={handleTiledChange} style={{ width: "100%" }}>
                        <div style={{ 
                            maxHeight: maxRows ? `${maxRows * 50}px` : 'none',
                            overflowY: maxRows ? 'auto' : 'visible'
                        }}>
                            <Row gutter={[6, 6]}>
                                {options.map((option) => (
                                    <Col span={24 / tiledColumns} key={option.value}>
                                        <Radio.Button
                                            value={option.value}
                                            style={{
                                                width: "100%",
                                                height: "40px",
                                                textAlign: "center",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "4px 2px",
                                            }}
                                        >
                                            {compactRenderLabel(option.airport)}
                                        </Radio.Button>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Radio.Group>
                </Spin>
            </div>
        );
    }

    return <Select showSearch placeholder="选择或搜索机场" loading={loading} options={options} optionFilterProp="searchValue" filterOption={filterOption} onChange={handleChange} {...restProps} style={{ ...restProps.style, margin: 0 }} />;
};

export default AirportSelector;
